import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { uniqueId } from '@aipb/shared/util/other';
import { FacetTreeNode } from '../../model';
import { Observable } from 'rxjs';

import { produce } from 'immer';
import { setDeep } from '../../facet.util';

export function write<S>(updater: (state: S) => void): (state: S) => S {
  return function (state) {
    return produce(state, (draft) => {
      updater(draft as S);
    });
  };
}

export interface CheckedProps {
  [key: string]: CheckedProps | boolean;
}

interface FacetTreeProps {
  facets: { [key: string]: FacetTreeNode };
  expandedFacetTreeNodes: Record<string, boolean>;
  checkedFacetItems: CheckedProps;
}

@Injectable()
export class FacetTreeStore {
  store = createStore(
    { name: 'facet-tree-' + uniqueId() },
    withProps<FacetTreeProps>({
      facets: {},
      expandedFacetTreeNodes: {},
      checkedFacetItems: {}
    })
  );

  facets$ = this.store.pipe(select((state) => state.facets));
  expandedFacetTreeNodes$ = this.store.pipe(select((state) => state.expandedFacetTreeNodes));
  checkedFacetItems$ = this.store.pipe(select((state) => state.checkedFacetItems));

  getCheckedFacetItems() {
    return this.store.getValue().checkedFacetItems;
  }

  getFacets() {
    return this.store.getValue().facets;
  }

  hasFacet(key: string): boolean {
    const facets = this.store.getValue().facets;

    return key in facets;
  }

  setFacet(key: string, facetTreeNode: FacetTreeNode) {
    this.store.update((state) => ({
      ...state,
      facets: {
        ...state.facets,
        [key]: facetTreeNode
      }
    }));
  }

  setFacetTreeNodeExpanded(id: string, value: boolean) {
    this.store.update((state) => ({
      ...state,
      expandedFacetTreeNodes: {
        ...state.expandedFacetTreeNodes,
        [id]: value
      }
    }));
  }

  setFacetTreeItemChecked(path: string[], value: boolean) {
    this.store.update(
      write((state) => {
        setDeep(state.checkedFacetItems, path, value);
      })
    );
  }

  selectFacet(key: string): Observable<FacetTreeNode> {
    return this.store.pipe(
      select((state) => {
        if (!(key in state.facets)) {
          return null;
        }

        return state.facets[key];
      })
    );
  }
}
